/* eslint-disable @typescript-eslint/no-explicit-any */
import { Close } from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { toast } from 'react-toastify';
    import input from "../../../../../assets/styles/components/input.module.css";
    import select from "../../../../../assets/styles/components/select.module.css";
    import containerModal from "../../../../../assets/styles/components/containerModal.module.css";
    import modal from "../../../../../assets/styles/components/modal.module.css";
import { type IPlanoContas, getSuggestedCode, registerOrEditPlanOfAccounts } from "../../../../../services/financial";

import styles from "./AddChartOfAccountsModal.module.css";
import Button from "../../../../../components/Button/Button";

interface AddPlanOfAccountsModalProps {
    onClose: () => void;
    refetch?: () => void;
    entryToEdit?: IPlanoContas;
    parentEntry?: IPlanoContas;
    onSuccess?: (message: string) => void;
}

interface PlanOfAccountsFormData {
    uid_plano_contas?: string;
    uid_plano_contas_pai?: string | null;
    codigo_classificacao: string;
    nome: string;
    tipo: 'receita' | 'despesa' | 'ambos';
    ativo: string;
}

const AddPlanOfAccountsModal = ({
    onClose,
    refetch,
    entryToEdit,
    parentEntry,
    onSuccess,
}: AddPlanOfAccountsModalProps) => {
    const queryClient = useQueryClient();
    const isEditing = !!entryToEdit;
    const isChildCreation = !!parentEntry && !isEditing;

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm<PlanOfAccountsFormData>({
        defaultValues: {
            nome: "",
            codigo_classificacao: "",
            tipo: "despesa",
            ativo: "true",
            uid_plano_contas_pai: null
        }
    });

    useEffect(() => {
        const fetchCode = async (parentId?: string | null) => {

            const code = await getSuggestedCode(parentId);
            if (code) {
                setValue("codigo_classificacao", code);
            }
        };
        if (entryToEdit) {
            setValue("uid_plano_contas", entryToEdit.uid_plano_contas);
            setValue("uid_plano_contas_pai", entryToEdit.uid_plano_contas_pai);
            setValue("nome", entryToEdit.nome);
            setValue("codigo_classificacao", entryToEdit.codigo_classificacao);
            setValue("tipo", entryToEdit.tipo);
            setValue("ativo", entryToEdit.ativo ? "true" : "false");
        } else if (parentEntry) {
            reset({
                nome: "",
                codigo_classificacao: `${parentEntry.codigo_classificacao}.`,
                tipo: parentEntry.tipo,
                ativo: "true",
                uid_plano_contas_pai: parentEntry.uid_plano_contas
            });
            fetchCode(parentEntry.uid_plano_contas);
        } else {
            reset({
                nome: "",
                codigo_classificacao: "",
                tipo: "despesa",
                ativo: "true",
                uid_plano_contas_pai: null
            });
            fetchCode(null);
        }
    }, [entryToEdit, parentEntry, setValue, reset]);

    const mutation = useMutation({
        mutationFn: registerOrEditPlanOfAccounts,
        onSuccess: () => {
            const successMessage = isEditing
                ? "Conta atualizada com sucesso!"
                : "Conta criada com sucesso!";

            onSuccess?.(successMessage);
            queryClient.invalidateQueries({ queryKey: ["planoContas"] });
            refetch?.();
            handleClose();
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.message || error.message || "Erro desconhecido.";
            toast.error(errorMessage);
        },
    });

    const onSubmit: SubmitHandler<PlanOfAccountsFormData> = async (data) => {
        try {
            const formattedData = {
                ...data,
                ativo: data.ativo === "true",
                uid_plano_contas_pai: isChildCreation ? parentEntry?.uid_plano_contas : data.uid_plano_contas_pai
            };

            await mutation.mutateAsync(formattedData as any);
        } catch (error) {
            console.error("Erro no formulário:", error);
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const getModalTitle = () => {
        if (isEditing) return "Editar Conta Contábil";
        if (isChildCreation) return "Nova Subconta";
        return "Nova Conta Contábil (Raiz)";
    };

    return (
        <form className={`${modal.containerModal} ${styles.containerProducts}`} onSubmit={handleSubmit(onSubmit)}>
            <div className={`${containerModal.containerModal} ${styles.products}`}>

                <h1>{getModalTitle()}</h1>

                {isChildCreation && (
                    <div style={{
                        backgroundColor: '#f0f9ff',
                        padding: '10px',
                        borderRadius: '6px',
                        marginBottom: '15px',
                        border: '1px solid #bae6fd',
                        color: '#0369a1',
                        fontSize: '0.9rem'
                    }}>
                        Adicionando subnível em: <strong>{parentEntry.codigo_classificacao} - {parentEntry.nome}</strong>
                    </div>
                )}

                <div className={containerModal.close}>
                    <Button variant="contain" typeStyle={["small", "cancel"]} onClick={handleClose} type="button">
                        <Close />
                    </Button>
                </div>

                <div className={styles.row}>
                    <div className={styles.fieldGroup} style={{ flex: 1 }}>
                        <label htmlFor="codigo">Código *</label>
                        <input
                            id="codigo"
                            className={input.input}
                            type="text"
                            placeholder="Ex: 1.01.001"
                            {...register("codigo_classificacao", { required: "O código é obrigatório" })}
                        />
                        {errors.codigo_classificacao && <span className={styles.errorText}>{errors.codigo_classificacao.message}</span>}
                    </div>

                    <div className={styles.fieldGroup} style={{ flex: 2 }}>
                        <label htmlFor="nome">Descrição *</label>
                        <input
                            id="nome"
                            className={input.input}
                            type="text"
                            placeholder="Descrição da conta"
                            {...register("nome", { required: "O nome é obrigatório" })}
                        />
                        {errors.nome && <span className={styles.errorText}>{errors.nome.message}</span>}
                    </div>
                </div>

                <div className={styles.row}>
                    <div className={styles.fieldGroup} style={{ flex: 1 }}>
                        <label htmlFor="tipo">Tipo</label>
                        <select
                            id="tipo"
                            className={`${input.input} ${select.select}`}
                            {...register("tipo")}
                        >
                            <option value="despesa">Despesa</option>
                            <option value="receita">Receita</option>
                        </select>
                    </div>

                    <div className={styles.fieldGroup} style={{ flex: 1 }}>
                        <label htmlFor="ativo">Status</label>
                        <select
                            id="ativo"
                            className={`${input.input} ${select.select}`}
                            {...register("ativo")}
                        >
                            <option value="true">Ativo</option>
                            <option value="false">Inativo</option>
                        </select>
                    </div>
                </div>

                <div className={styles.buttonSave}>
                    <Button variant="contain" type="submit" disabled={mutation.isPending}>
                        {mutation.isPending ? "Salvando..." : "Salvar"}
                    </Button>
                </div>
            </div>
        </form>
    );
};

export default AddPlanOfAccountsModal;